import { Button } from "@/components/button";
import { DigitInput } from "@/components/digit-input";
import { ErrorMessage } from "@/components/form/errors";
import { Icon } from "@/components/icon";
import { Input } from "@/components/input";
import { LoginSignupStep } from "@/routes/login-signup/-reducer";
import { Dispatch, FormEvent, SetStateAction } from "react";
import { z } from "zod";

// eslint-disable-next-line no-useless-escape
const telSchema = z.string().regex(/^[0-9\.\,\-\+\s\(\)]*$/);

export type OtpVerifier = "email" | "sms" | "external";

interface OtpLoginFormProps {
  verifier: OtpVerifier;
  onChangeVerifier: (verifier: OtpVerifier) => void;
  value: string;
  otp: string;
  onChangeValue: (value: string) => void;
  onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
  onOtpChange: (value: string) => void;
  onOtpComplete: (value: string) => void;
  onResend: () => void;
  step: LoginSignupStep;
  connectError?: string;
  isSuccess?: boolean;
  isFailure?: boolean;
  isContinueDisabled?: boolean;
}

export function OtpLoginForm({
  verifier = "email",
  onChangeVerifier,
  value,
  otp,
  onChangeValue,
  onOtpChange,
  onSubmit,
  onOtpComplete,
  onResend,
  step,
  isContinueDisabled,
  connectError,
  isSuccess,
  isFailure,
}: OtpLoginFormProps) {
  const parseTel = (next: string) => {
    try {
      telSchema.parse(next);
      return next;
    } catch {
      return value;
    }
  };

  const onChangeOtp: Dispatch<SetStateAction<string>> = (
    value: SetStateAction<string>,
  ) => {
    const code = typeof value === "function" ? value(otp) : value;
    onOtpChange(code);
    if (code.length === 6) {
      onOtpComplete(code);
    }
  };

  return step === "login-signup" ? (
    <div className="flex flex-col">
      {connectError && (
        <ErrorMessage
          variant="banner"
          className="w-screen absolute left-0 -translate-y-[50%]"
        >
          {connectError}
        </ErrorMessage>
      )}
      <div className="flex items-center justify-center my-8">
        <Icon name="dflow-logo" color="orchid" />
      </div>

      <form className="flex flex-col gap-2" onSubmit={onSubmit}>
        {verifier === "email" ? (
          <>
            <Input
              type="email"
              placeholder="Enter email"
              value={value}
              onChange={(e) => onChangeValue(e.target.value)}
            />

            <Button type="submit" disabled={isContinueDisabled}>
              Continue
            </Button>
          </>
        ) : verifier === "sms" ? (
          <>
            <Input
              type="tel"
              placeholder="Enter phone number (US/Canada only)"
              value={value}
              onChange={(e) => onChangeValue(parseTel(e.target.value))}
            />
            <Button type="submit" disabled={isContinueDisabled}>
              Continue
            </Button>
          </>
        ) : null}
        <span className="flex items-center justify-between text-sm">
          {verifier === "email"
            ? "Prefer phone number sign up?"
            : "Prefer email sign up?"}{" "}
          <button
            type="button"
            className="uppercase font-bold text-primary"
            onClick={() =>
              onChangeVerifier(verifier === "email" ? "sms" : "email")
            }
          >
            Use {verifier === "email" ? "Phone" : "Email"}
          </button>
        </span>
      </form>
    </div>
  ) : step === "verify" ? (
    <div className="flex flex-col items-center gap-6">
      <div className="relative flex justify-center items-center">
        <div className="my-5 flex justify-center items-center relative">
          {!isSuccess && !isFailure ? (
            <>
              <Icon
                name="circle-partial"
                className="w-[50px] h-[50px] text-primary absolute animate-spin"
              />
              <Icon name="email" className="w-6 h-6 text-primary" />
            </>
          ) : null}
          {isSuccess ? (
            <>
              <div className="w-[50px] h-[50px] rounded-full border-2 border-positive absolute" />
              <Icon name="check" className="w-6 h-6 text-positive" />
            </>
          ) : null}
          {isFailure ? (
            <>
              <div className="w-[50px] h-[50px] rounded-full border-2 border-negative absolute" />
              <Icon name="warning" className="w-6 h-6 text-primary" />
            </>
          ) : null}
        </div>
      </div>
      <p className="text-gray-200 text-sm text-center">
        We&apos;ve sent a verification code to
        <br />
        <span className="font-bold">{value}</span>
      </p>
      <DigitInput
        value={otp}
        inputMode="numeric"
        isError={isFailure}
        isSuccess={isSuccess}
        onChange={onChangeOtp}
      />
      <div className="flex flex-col gap-2">
        <p className="text-gray-200 text-sm text-center">
          Did not receive your code yet?
        </p>
        <button className="uppercase text-primary font-bold" onClick={onResend}>
          Resend Code
        </button>
      </div>
    </div>
  ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  step === "beta-access-denied" ? (
    <>
      <div>
        <svg
          width="107"
          height="152"
          viewBox="0 0 107 152"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="mx-auto mb-8"
        >
          <g filter="url(#filter0_d_11941_23026)">
            <path
              d="M18.6001 2.1001L17.0001 3.0251L7.4001 8.56885L5.8001 9.4876V11.3376V29.8126V31.6563L7.4001 32.5813L9.0001 33.5063V63.0688L4.2001 65.8376L2.6001 66.7626V68.6063V101.863V103.706L4.2001 104.631L33.0001 121.263L34.6001 122.188L36.2001 121.263L41.0001 118.488L93.8001 148.975L95.4001 149.9L97.0001 148.975L103.4 145.281L105 144.356V142.506V53.8251V51.9751L103.4 51.0501L95.4001 46.4376V40.8938V39.0501L93.8064 38.1251L90.6063 36.2751L89.0001 35.3438L87.3939 36.2751L82.5938 39.0563L44.2001 16.8751V11.3251V9.46885L42.5813 8.5501L39.3813 6.7251L37.7938 5.8126L36.2063 6.7251L31.4063 9.4876L20.2001 3.0251L18.6001 2.1001ZM18.6001 6.71885L29.8063 13.1876L21.8001 17.8001L10.6001 11.3376L18.6001 6.71885ZM37.8001 10.4251L39.3938 11.3313L25.0001 19.6501L23.4001 18.7251L31.7688 13.8938C31.7794 13.8878 31.7898 13.8816 31.8001 13.8751L37.8001 10.4251ZM40.2001 12.7126V17.3376L38.2001 16.1876C38.0785 16.1174 37.9405 16.0804 37.8001 16.0804C37.6597 16.0804 37.5217 16.1174 37.4001 16.1876L27.8001 21.7313C27.6784 21.8016 27.5773 21.9028 27.5071 22.0246C27.4368 22.1464 27.3999 22.2845 27.4001 22.4251V29.3501L25.8001 30.2751V21.0376L40.2001 12.7126ZM9.8001 12.7251L21.0001 19.1876V29.8126C20.9999 29.9532 21.0368 30.0913 21.1071 30.2131C21.1773 30.3349 21.2784 30.436 21.4001 30.5063L24.6001 32.3563C24.7217 32.4266 24.8597 32.4635 25.0001 32.4635C25.1405 32.4635 25.2785 32.4266 25.4001 32.3563L28.6001 30.5063C28.7218 30.436 28.8229 30.3349 28.8931 30.2131C28.9634 30.0913 29.0003 29.9532 29.0001 29.8126V23.8063L72.2001 48.7438V59.3751C72.1999 59.5157 72.2368 59.6538 72.3071 59.7756C72.3773 59.8974 72.4784 59.9986 72.6001 60.0688L75.8001 61.9126C75.9217 61.9828 76.0597 62.0198 76.2001 62.0198C76.3405 62.0198 76.4785 61.9828 76.6001 61.9126L79.8001 60.0688C79.9218 59.9986 80.0229 59.8974 80.0931 59.7756C80.1634 59.6538 80.2003 59.5157 80.2001 59.3751V53.3688L91.4001 59.8313V76.4626L12.6313 30.9876C12.6211 30.9811 12.6107 30.9749 12.6001 30.9688L9.8001 29.3501V12.7251ZM37.8001 17.8063L40.5688 19.3938C40.5791 19.4003 40.5895 19.4066 40.6001 19.4126L81.0001 42.7501L73.0001 47.3626L29.7938 22.4251L37.8001 17.8063ZM22.6001 20.1126L24.2001 21.0376V30.2751L22.6001 29.3501V20.1126ZM13.0001 33.0501L91.7001 78.4751C91.7318 78.4984 91.7653 78.5193 91.8001 78.5376C91.9217 78.6078 92.0597 78.6448 92.2001 78.6448C92.3405 78.6448 92.4785 78.6078 92.6001 78.5376L94.6001 77.3813V144.819L41.8001 114.338V81.5438C41.8003 81.4033 41.7634 81.2651 41.6931 81.1433C41.6229 81.0215 41.5218 80.9204 41.4001 80.8501L13.0001 64.4563V33.0501ZM89.0001 39.9626L90.6063 40.8938L76.2001 49.2126L74.6001 48.2876L82.9688 43.4626C82.9794 43.4566 82.9898 43.4503 83.0001 43.4438L89.0001 39.9626ZM91.4001 42.2813V46.9001L89.4001 45.7438C89.2785 45.6736 89.1405 45.6367 89.0001 45.6367C88.8597 45.6367 88.7217 45.6736 88.6001 45.7438L79.0001 51.2876C78.8783 51.3579 78.7773 51.459 78.7071 51.5808C78.6368 51.7026 78.5999 51.8408 78.6001 51.9813V58.9126L77.0001 59.8313V50.5938L91.4001 42.2813ZM89.0001 47.3626L91.7689 48.9626C91.7791 48.9691 91.7895 48.9753 91.8001 48.9813L100.2 53.8251L92.2001 58.4501L81.0001 51.9813L89.0001 47.3626ZM24.9376 49.3376C24.7364 49.3534 24.5487 49.4446 24.4119 49.5929C24.2752 49.7413 24.1995 49.9358 24.2001 50.1376V64.9126C24.1999 65.0532 24.2368 65.1913 24.3071 65.3131C24.3773 65.4349 24.4784 65.536 24.6001 65.6063L37.4001 72.9939C37.5213 73.0638 37.6588 73.1008 37.7988 73.101C37.9387 73.1012 38.0763 73.0647 38.1978 72.9951C38.3192 72.9255 38.4203 72.8253 38.4909 72.7045C38.5615 72.5836 38.5991 72.4463 38.6001 72.3064V57.5251C38.6003 57.3845 38.5634 57.2464 38.4931 57.1246C38.4229 57.0028 38.3218 56.9016 38.2001 56.8313L25.4001 49.4438C25.2602 49.3625 25.099 49.3254 24.9376 49.3376ZM73.8001 49.6688L75.4001 50.5938V59.8313L73.8001 58.9126V49.6688ZM25.8001 51.5188L37.0001 57.9876V70.9188L25.8001 64.4563V51.5188ZM101 55.2126V71.8438L93.0001 76.4626V59.8376L101 55.2126ZM47.3376 62.2688C47.1364 62.2846 46.9487 62.3758 46.8119 62.5242C46.6752 62.6725 46.5995 62.8671 46.6001 63.0688V77.8438C46.5999 77.9844 46.6368 78.1226 46.7071 78.2444C46.7773 78.3662 46.8784 78.4673 47.0001 78.5376L59.8001 85.9314C59.9218 86.0016 60.0598 86.0385 60.2003 86.0385C60.3408 86.0384 60.4788 86.0014 60.6005 85.9311C60.7221 85.8607 60.8231 85.7596 60.8932 85.6379C60.9634 85.5162 61.0002 85.3781 61.0001 85.2376V70.4564C61.0003 70.3158 60.9634 70.1776 60.8931 70.0558C60.8229 69.934 60.7218 69.8329 60.6001 69.7626L47.8001 62.3751C47.6602 62.2937 47.499 62.2567 47.3376 62.2688ZM48.2001 64.4563L59.4001 70.9188V83.8501L48.2001 77.3876V64.4563ZM12.2001 65.8376L39.4001 81.5438L34.6001 84.3126L7.4001 68.6126L12.2001 65.8376ZM6.6001 70.0001L33.8001 85.7001V117.106L6.6001 101.406V70.0001ZM8.9376 73.3563C8.73644 73.3721 8.54868 73.4633 8.41192 73.6117C8.27516 73.76 8.1995 73.9546 8.2001 74.1563V100.019C8.19994 100.159 8.23683 100.298 8.30706 100.419C8.37728 100.541 8.47835 100.642 8.6001 100.713L31.0001 113.644C31.1218 113.714 31.2598 113.751 31.4003 113.751C31.5408 113.751 31.6788 113.714 31.8005 113.644C31.9221 113.573 32.0231 113.472 32.0932 113.35C32.1634 113.229 32.2002 113.091 32.2001 112.95V87.0876C32.2003 86.947 32.1634 86.8089 32.0931 86.6871C32.0229 86.5653 31.9218 86.4641 31.8001 86.3939L9.4001 73.4626C9.26022 73.3812 9.09896 73.3442 8.9376 73.3563ZM101 73.6876V142.05L96.2001 144.819V76.4564L101 73.6876ZM69.7376 75.2001C69.5364 75.2159 69.3487 75.3071 69.2119 75.4554C69.0752 75.6038 68.9995 75.7983 69.0001 76.0001V90.7813C68.9999 90.9219 69.0368 91.0601 69.1071 91.1819C69.1773 91.3037 69.2784 91.4048 69.4001 91.4751L82.2001 98.8626C82.3218 98.9328 82.4598 98.9698 82.6003 98.9697C82.7408 98.9697 82.8788 98.9326 83.0005 98.8623C83.1221 98.792 83.2231 98.6909 83.2932 98.5691C83.3634 98.4474 83.4002 98.3093 83.4001 98.1688V83.3876C83.4003 83.247 83.3634 83.1089 83.2931 82.9871C83.2229 82.8653 83.1218 82.7641 83.0001 82.6938L70.2001 75.3064C70.0602 75.225 69.899 75.1879 69.7376 75.2001ZM9.8001 75.5376L30.6001 87.5438V111.563L9.8001 99.5564V75.5376ZM70.6001 77.3876L81.8001 83.8501V96.7813L70.6001 90.3188V77.3876ZM18.5376 82.5876C18.3364 82.6034 18.1487 82.6946 18.0119 82.8429C17.8752 82.9913 17.7995 83.1858 17.8001 83.3876V94.4751C17.7999 94.6157 17.8368 94.7538 17.9071 94.8756C17.9773 94.9974 18.0784 95.0985 18.2001 95.1688L24.6001 98.8626C24.72 98.9319 24.8557 98.9688 24.9942 98.9699C25.1326 98.971 25.2689 98.9361 25.3899 98.8687C25.5108 98.8013 25.6121 98.7037 25.6841 98.5854C25.756 98.4671 25.7959 98.3322 25.8001 98.1938L25.9063 94.4688C25.9098 94.3228 25.8731 94.1786 25.8004 94.0519C25.7277 93.9252 25.6217 93.8208 25.4938 93.7501L22.6001 92.1563V85.2376C22.6003 85.097 22.5634 84.9589 22.4931 84.8371C22.4229 84.7153 22.3218 84.6142 22.2001 84.5438L19.0001 82.6938C18.8839 82.6271 18.7528 82.5905 18.6188 82.5876C18.5918 82.5862 18.5647 82.5862 18.5376 82.5876ZM40.2001 82.9314V114.338L35.4001 117.106V85.7001L40.2001 82.9314ZM19.4001 84.7751L21.0001 85.7001V92.6251C21.0001 92.768 21.0383 92.9083 21.1109 93.0314C21.1834 93.1545 21.2876 93.2559 21.4126 93.3251L24.2938 94.9126L24.2376 96.8064L19.4001 94.0126V84.7751ZM47.3376 88.1313C47.1364 88.1471 46.9487 88.2383 46.8119 88.3867C46.6752 88.535 46.5995 88.7296 46.6001 88.9314V103.713C46.5999 103.853 46.6368 103.991 46.7071 104.113C46.7773 104.235 46.8784 104.336 47.0001 104.406L59.8001 111.794C59.9213 111.864 60.0588 111.901 60.1988 111.901C60.3387 111.901 60.4763 111.865 60.5978 111.795C60.7192 111.726 60.8203 111.625 60.8909 111.504C60.9615 111.384 60.9991 111.246 61.0001 111.106V96.3251C61.0003 96.1845 60.9634 96.0464 60.8931 95.9246C60.8229 95.8028 60.7218 95.7016 60.6001 95.6313L47.8001 88.2376C47.6602 88.1562 47.499 88.1192 47.3376 88.1313ZM48.2001 90.3188L59.4001 96.7876V109.719L48.2001 103.25V90.3188ZM69.7376 101.069C69.5375 101.085 69.3506 101.175 69.214 101.322C69.0774 101.469 69.0011 101.662 69.0001 101.863V116.644C68.9999 116.784 69.0368 116.923 69.1071 117.044C69.1773 117.166 69.2784 117.267 69.4001 117.338L82.2001 124.725C82.3213 124.795 82.4588 124.832 82.5988 124.832C82.7387 124.832 82.8763 124.796 82.9978 124.726C83.1192 124.657 83.2203 124.557 83.2909 124.436C83.3615 124.315 83.3991 124.178 83.4001 124.038V109.256C83.4003 109.116 83.3634 108.978 83.2931 108.856C83.2229 108.734 83.1218 108.633 83.0001 108.563L70.2001 101.175C70.0602 101.094 69.899 101.057 69.7376 101.069ZM70.6001 103.25L81.8001 109.719V122.65L70.6001 116.188V103.25Z"
              fill="#C5D0AD"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_11941_23026"
              x="0.600098"
              y="0.100098"
              width="106.4"
              height="151.8"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset />
              <feGaussianBlur stdDeviation="1" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.772549 0 0 0 0 0.815686 0 0 0 0 0.678431 0 0 0 0.7 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_11941_23026"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_11941_23026"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
      </div>
      <p className="text-gray-200 text-sm text-center">
        Our app is currently in beta and available to select users only, but we
        can&apos;t wait to welcome you soon!
      </p>
    </>
  ) : null;
}
